import { useMutation } from "convex/react";
import { Ellipsis } from "lucide-react";
import { motion } from "motion/react";
import type { Ref } from "react";
import { memo, useState } from "react";

import { MoveCardDialogContent } from "@/components/MoveCardDialogContent";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { EditCardDialogContent } from "@/components/EditCardDialogContent";

export interface DeckCardProps {
  id?: Id<"cards">;
  deckId: Id<"decks">;
  question?: string;
  answer?: string;
  singleSided?: boolean;
  ref?: Ref<HTMLDivElement>; // This is necessary to get the AnimatePresence 'popLayout' mode working correctly - see: https://motion.dev/docs/react-animate-presence#mode
}

export const DeckCard = memo(
  ({ id, deckId, question, answer, singleSided, ref }: DeckCardProps) => {
    const [moveCardDialogOpen, setMoveCardDialogOpen] = useState(false);
    const [editCardDialogOpen, setEditCardDialogOpen] = useState(false);

    const updateCardMutation = useMutation(api.cards.update).withOptimisticUpdate(
      (localStore, { id, answer, question, singleSided }) => {
        // TODO: this will spam the server with updates if the user types quickly. We should find a way to debounce it.
        const allCards = localStore.getQuery(api.cards.getByDeck, { deckId }) ?? [];
        const currentCardIndex = allCards.findIndex((c) => c._id === id);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- we just used findIndex to get a real index
        const currentCard = allCards[currentCardIndex]!;
        if (currentCardIndex !== -1) {
          const update = {
            ...currentCard,
            ...(question !== undefined && { question }),
            ...(answer !== undefined && { answer }),
            ...(singleSided !== undefined && { singleSided }),
          };
          const updatedCards = [...allCards];
          updatedCards.splice(currentCardIndex, 1, update);
          localStore.setQuery(api.cards.getByDeck, { deckId }, updatedCards);
        }
      }
    );

    const updateQuestion = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      // If we don't have a card id, there's nothing to update (because we're rendering a placeholder)
      if (id) {
        void updateCardMutation({ id, question: event.target.value });
      }
    };

    const updateAnswer = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      // If we don't have a card id, there's nothing to update (because we're rendering a placeholder)
      if (id) {
        void updateCardMutation({ id, answer: event.target.value });
      }
    };

    const toggleSingleSided = () => {
      // If we don't have a card id, there's nothing to update (because we're rendering a placeholder)
      if (id) {
        void updateCardMutation({ id, singleSided: !singleSided });
      }
    };

    const removeCardMutation = useMutation(api.cards.remove);
    const removeCard = () => {
      if (id) {
        void removeCardMutation({ id });
      }
    };

    return (
      <motion.div
        ref={ref}
        className="w-64 h-56 p-4 border rounded shadow-sm flex flex-col justify-between bg-white hover:shadow-md transition-shadow relative"
        layout
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{
          type: "tween",
        }}
      >
        <div className="font-medium mb-2 flex-1 overflow-hidden relative">
          <textarea
            value={question}
            onChange={updateQuestion}
            className="w-full h-full resize-none outline-none font-medium"
            style={{ overflow: "auto" }}
          />
          <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
        </div>
        {!singleSided && (
          <>
            <hr className="border-gray-200 my-2 w-full" />
            <div className="text-gray-600 flex-1 overflow-hidden relative">
              <textarea
                value={answer}
                onChange={updateAnswer}
                className="w-full h-full resize-none outline-none text-gray-600"
                style={{ overflow: "auto" }}
              />

              <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
            </div>
          </>
        )}
        {id && (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon-md" variant="ghost" className="absolute bottom-2 right-2">
                  <Ellipsis />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-32">
                <DropdownMenuItem onSelect={() => setEditCardDialogOpen(true)}>
                  Edit card
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setMoveCardDialogOpen(true)}>
                  Move to another deck
                </DropdownMenuItem>
                <DropdownMenuItem onClick={toggleSingleSided}>
                  {singleSided ? "Convert to two-sided card" : "Convert to single-sided card"}
                </DropdownMenuItem>
                <DropdownMenuItem variant="destructive" onClick={removeCard}>
                  Delete card
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Dialog open={moveCardDialogOpen} onOpenChange={setMoveCardDialogOpen}>
              <MoveCardDialogContent currentDeckId={deckId} cardId={id} />
            </Dialog>
            <Dialog open={editCardDialogOpen} onOpenChange={setEditCardDialogOpen}>
              <EditCardDialogContent
                cardId={id}
                initialQuestion={question}
                initialAnswer={answer}
                initialSingleSided={singleSided}
              />
            </Dialog>
          </>
        )}
      </motion.div>
    );
  }
);
